import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import {
  EmployeePlanInvitationDataAccessService,
  EmployeeSettingsService,
} from '@flexyoo/employee/data-access';
import { UserAgreementService } from '@flexyoo/employee/ui';
import {
  getEmployeeDefaultRoute,
  isCafeteriaPlanEnrolled,
  isCafeteriaPlanInvited,
  isSamePathname,
} from '@flexyoo/employee/util';
import { map, of, switchMap } from 'rxjs';
import { RoleEnum } from 'src/app/shared/models/general/general-enum';

@Injectable()
export class CafeteriaUserAgreementGuard {
  private readonly employeePlanInvitationService = inject(EmployeePlanInvitationDataAccessService);

  constructor(
    protected readonly router: Router,
    private readonly auth: AuthenticationService,
    private readonly userAgreementService: UserAgreementService,
    private readonly employeeSettingsService: EmployeeSettingsService
  ) {}

  public canActivate(
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): MaybeAsync<GuardResult> {
    const user = this.auth.getCurrentUser();

    if (!user) {
      this.auth.logout();
      return false;
    }

    if (user.Role === RoleEnum.Employee) {
      return this.employeeSettingsService.updateAndLoadEmployeeSettings(user.Id).pipe(
        switchMap(({ employeeSettings, tenantSettings }) => {
          if (!employeeSettings || !tenantSettings) {
            return of(false);
          }

          return this.employeePlanInvitationService
            .loadEmployeePlanInvitation(user.EmployeeId)
            .pipe(
              map((invitation) => {
                // Not enrolled or not invited to cafeteria: do not force signing; go to default plan.
                if (
                  !isCafeteriaPlanEnrolled(employeeSettings) ||
                  !isCafeteriaPlanInvited(invitation)
                ) {
                  const fallbackRoute = getEmployeeDefaultRoute(
                    employeeSettings,
                    tenantSettings,
                    invitation
                  );
                  if (
                    isSamePathname(state.url, fallbackRoute) ||
                    isSamePathname(this.router.url, fallbackRoute)
                  ) {
                    // Already on the fallback: allow it. Navigating to the same URL
                    // re-runs this guard and loops GET /employees/{id} (e.g. HR proxy).
                    return true;
                  }

                  void this.router.navigate([fallbackRoute]);
                  return false;
                }

                // Plan-specific: cafeteria routes require cafeteria agreement when enabled.
                const userAgreementState =
                  this.userAgreementService.checkCafeteriaUserAgreementState(
                    employeeSettings,
                    tenantSettings
                  );
                switch (userAgreementState) {
                  case 'to-be-uploaded': {
                    if (state.url.includes('employee-account')) {
                      return true;
                    }
                    this.router.navigate(['/employee-account']);
                    return false;
                  }
                  case 'sign-agreement-expired':
                  case 'under-validation':
                    this.router.navigate(['/login']);
                    return false;
                  case 'ok':
                    return true;
                }
              })
            );
        })
      );
    }

    return true;
  }
}
